<template>
  <div
    v-if="iconName"
    class="display-flex flex-row flex-wrap flex-items-stretch icon-actions"
    style="--button-margin-bottom: 0; gap: var(--spacing-md)"
  >
    <site-link
      v-if="gettingStartedURL"
      :to="gettingStartedURL"
      :class="startUsingActionClass"
      :aria-label="startUsingActionToolTip"
      :style="actionStyle"
      data-balloon-pos="up"
      @click.native="getStartedLink"
    >
      <font-awesome-icon
        :icon="$v6.regular.faRocketLaunch"
        fixed-width
        size="xl"
      />
      <span class="margin-left-2xs">{{ getStartedLabel }}</span>
    </site-link>
    <button
      v-else
      :class="startUsingActionClass"
      :aria-label="startUsingActionToolTip"
      data-balloon-pos="up"
      :style="actionStyle"
      style="flex-grow: 1"
      @click.prevent="getStarted"
    >
      <font-awesome-icon
        :icon="$v6.regular.faRocketLaunch"
        fixed-width
        size="xl"
      />
      <span class="margin-left-2xs">{{ getStartedLabel }}</span>
    </button>
    <button
      v-if="editable"
      class="position-relative icon-action-customize"
      aria-label="Magically add modifiers to this icon!"
      data-balloon-pos="up"
      :style="actionStyle"
      style="flex-grow: 0; padding-right: var(--spacing-md)"
      @click.prevent="editIcon()"
    >
      <font-awesome-icon
        :icon="$v6.solid.faSquareDashedCirclePlus"
        fixed-width
        size="xl"
      />
      <span class="sr-only">Add Modifiers with the Icon Wizard</span>
    </button>
    <button
      v-if="isCustomIcon"
      class="position-relative icon-action-svg-download"
      :style="actionStyle"
      :aria-label="`Delete ${icon.name}`"
      data-balloon-pos="up"
      @click.prevent="$emit('showConfirmDelete')"
    >
      <font-awesome-icon
        :icon="$v6.solid.faTrashCan"
        fixed-width
        size="lg"
      />
      <span class="sr-only">Delete {{ icon.name }}</span>
    </button>
  </div>
</template>
<script>
  import { FAEditableFamilyIds, FAEditableStyleIds, FAEditableIconNameExclusions } from '@fortawesome-internal-tools/fontawesome-icons/canonical' // eslint-disable-line import/no-unresolved
  import { EventTrackingMixin } from 'app/mixins'
  import { merge } from 'lodash'

  const EDITABLE_FAMILIES = FAEditableFamilyIds
  const EDITABLE_STYLES = FAEditableStyleIds
  const EDITABLE_NAME_OMITLIST = FAEditableIconNameExclusions // Using String.prototype.includes() as a test

  export default {
    name: 'IconActions',

    mixins: [EventTrackingMixin],

    inject: ['currentVersion'],

    props: {
      canAccessIcon: {
        type: Boolean
      },
      canUseIcon: {
        type: Boolean
      },
      icon: {
        type: Object
      },
      isCustomIcon: {
        type: Boolean
      },
      isProUser: {
        type: Boolean,
        required: true
      },
      isProIcon: {
        type: Boolean,
        required: true
      },
      isProPlusIcon: {
        type: Boolean,
        required: true
      },
      isUsingModal: {
        type: Boolean,
        required: true
      },
      styling: {
        type: Object
      }
    },

    data() {
      return {
        downloadStarted: false,
        startUsingActionClass: 'button accent position-relative icon-action-start-using',
        startUsingActionToolTip: 'Set up your web project to use this icon!',
        viggo: null
      }
    },

    computed: {
      actionStyle() {
        return {
          ...{
            top: 'calc(var(--button-box-shadow-width) * -1)'
          }
        }
      },

      editable() {
        return (
          this.$releases.active.versionParts.major >= 6 &&
          EDITABLE_FAMILIES.includes(this.styling?.family) &&
          EDITABLE_STYLES.includes(this.styling?.style) &&
          !EDITABLE_NAME_OMITLIST.some((omitString) => this.iconName.includes(omitString))
        )
      },

      iconName() {
        return this.icon?.name
      },

      isSignedIn() {
        return this.$route.meta.isSignedIn
      },

      getStartedLabel() {
        if (this.isProPlusIcon) return 'Start Using This Pro+ Icon'
        if (this.isProIcon) return 'Start Using This Pro Icon'

        return 'Start Using This Icon'
      },

      gettingStartedURL() {
        let url = ''

        if (this.shouldLinkToStart) {
          url = this.$router.resolve({ name: 'start' }).href
        } else if (this.shouldLinkToDocs) {
          url = this.$router.resolve({ name: `${this.currentVersion.name}.docs`, params: { pathMatch: '/web/setup/get-started/' } }).href
        }

        return url
      },

      shouldLinkToDocs() {
        return this.canUseIcon
      },

      shouldLinkToStart() {
        return !this.isProIcon && !this.isSignedIn
      }
    },

    methods: {
      getStarted() {
        this.$posthog.capture(
          'get_started_button_click',
          merge(
            {
              context: `${this.iconModalContext(this.isUsingModal)}`,
              icon_name: this.icon.name,
              icon_style: this.styling.style,
              icon_family: this.styling.family
            },
            this.determinationsEventProps
          )
        )

        this.$emit('getSelectedAction', 'Using this Icon')

        this.$emit('openProIconModal')
      },

      getStartedLink() {
        this.$posthog.capture(
          'get_started_button_click',
          merge(
            {
              context: `${this.iconModalContext(this.isUsingModal)}`,
              icon_name: this.icon.name,
              icon_style: this.styling.style,
              icon_family: this.styling.family
            },
            this.determinationsEventProps
          )
        )
      },

      editIcon() {
        this.$emit('editIcon')
        this.$posthog.capture('icon_wizard_button_click', {
          context: `${this.iconModalContext(this.isUsingModal)}`,
          icon_name: this.icon.name,
          icon_style: this.styling.style,
          icon_family: this.styling.family
        })
      }
    }
  }
</script>
